import React from "react";
import { MatchOfficialsDisplay } from "../MatchOfficials";
import ScoreComparisonGraph from "../ScoreComparisonGraph";

interface InfoTabProps {
  match: {
    teamA: string;
    teamB: string;
    format: string;
    tossDisplay?: string | null;
    battingFirstTeam?: string | null;
    venue?: string | null;
    matchDate?: string | null;
    matchTime?: string | null;
    officials?: any;
    matchData?: any;
  };
}

function getFormatLabel(format: string): string {
  if (format === "INDOOR") return "Indoor";
  if (format === "T20") return "T20";
  if (format === "ODI") return "ODI (50 Overs)";
  if (format === "TEST") return "Test";
  return format || "Cricket";
}

export default function InfoTab({ match }: InfoTabProps) {
  let dateDisplay: string | null = null;
  if (match.matchDate) {
    try {
      const d = new Date(match.matchDate);
      dateDisplay = d.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      dateDisplay = match.matchDate;
    }
  }

  const formatLabel = getFormatLabel(match.format);

  const hasGraph = !!(match.matchData?.innings?.length > 0);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 shadow-xl overflow-hidden">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-slate-400 text-base mt-0.5">🏏</span>
            <div>
              <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Match Format</div>
              <div className="text-white text-sm font-semibold">{formatLabel}</div>
            </div>
          </div>

          {match.tossDisplay && (
            <div className="flex items-start gap-3">
              <span className="text-amber-400 text-base mt-0.5">🪙</span>
              <div>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Toss</div>
                <div className="text-white text-sm">{match.tossDisplay}</div>
              </div>
            </div>
          )}

          {match.battingFirstTeam && (
            <div className="flex items-start gap-3">
              <span className="text-green-400 text-base mt-0.5">🏏</span>
              <div>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Batting First</div>
                <div className="text-white text-sm font-semibold">{match.battingFirstTeam}</div>
              </div>
            </div>
          )}

          {match.venue && (
            <div className="flex items-start gap-3">
              <span className="text-blue-400 text-base mt-0.5">📍</span>
              <div>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Venue</div>
                <div className="text-white text-sm">{match.venue}</div>
              </div>
            </div>
          )}

          {dateDisplay && (
            <div className="flex items-start gap-3">
              <span className="text-slate-400 text-base mt-0.5">📅</span>
              <div>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Date & Time</div>
                <div className="text-white text-sm">
                  {dateDisplay}
                  {match.matchTime && <span className="text-slate-400"> at {match.matchTime}</span>}
                </div>
              </div>
            </div>
          )}

          <MatchOfficialsDisplay officials={match.officials} className="pt-1" />

          {!match.tossDisplay && !match.venue && !dateDisplay && (
            <div className="text-slate-400 text-sm text-center py-1">
              Match details will appear here once set up
            </div>
          )}
        </div>
      </div>

      {hasGraph && (
        <div>
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider px-1 mb-3">Score Progression</h3>
          <ScoreComparisonGraph state={match.matchData} />
        </div>
      )}
    </div>
  );
}
